import { memo } from 'react';
import { type NodeProps } from 'reactflow';
import { Paperclip, FileText, FileImage, FileType, UploadCloud } from 'lucide-react';
import { BaseNodeUI } from './BaseNodeUI';

// ── Extension → display config ─────────────────────────────────────────────
const EXT_CONFIG: Record<string, { icon: any; badge: string; bg: string; badgeBg: string; badgeText: string }> = {
    pdf:  { icon: FileText,  badge: 'PDF',   bg: 'from-red-50 to-white border-red-200',      badgeBg: 'bg-red-100',    badgeText: 'text-red-700' },
    docx: { icon: FileType,  badge: 'DOCX',  bg: 'from-blue-50 to-white border-blue-200',    badgeBg: 'bg-blue-100',   badgeText: 'text-blue-700' },
    doc:  { icon: FileType,  badge: 'DOC',   bg: 'from-blue-50 to-white border-blue-200',    badgeBg: 'bg-blue-100',   badgeText: 'text-blue-700' },
    csv:  { icon: FileText,  badge: 'CSV',   bg: 'from-emerald-50 to-white border-emerald-200', badgeBg: 'bg-emerald-100', badgeText: 'text-emerald-700' },
    xlsx: { icon: FileText,  badge: 'Excel', bg: 'from-green-50 to-white border-green-200',  badgeBg: 'bg-green-100',  badgeText: 'text-green-700' },
    xls:  { icon: FileText,  badge: 'Excel', bg: 'from-green-50 to-white border-green-200',  badgeBg: 'bg-green-100',  badgeText: 'text-green-700' },
    png:  { icon: FileImage, badge: 'PNG',   bg: 'from-purple-50 to-white border-purple-200',badgeBg: 'bg-purple-100', badgeText: 'text-purple-700' },
    jpg:  { icon: FileImage, badge: 'JPG',   bg: 'from-purple-50 to-white border-purple-200',badgeBg: 'bg-purple-100', badgeText: 'text-purple-700' },
    jpeg: { icon: FileImage, badge: 'JPEG',  bg: 'from-purple-50 to-white border-purple-200',badgeBg: 'bg-purple-100', badgeText: 'text-purple-700' },
    gif:  { icon: FileImage, badge: 'GIF',   bg: 'from-pink-50 to-white border-pink-200',   badgeBg: 'bg-pink-100',   badgeText: 'text-pink-700' },
    webp: { icon: FileImage, badge: 'WebP',  bg: 'from-pink-50 to-white border-pink-200',   badgeBg: 'bg-pink-100',   badgeText: 'text-pink-700' },
    tiff: { icon: FileImage, badge: 'TIFF',  bg: 'from-indigo-50 to-white border-indigo-200',badgeBg: 'bg-indigo-100', badgeText: 'text-indigo-700' },
    tif:  { icon: FileImage, badge: 'TIFF',  bg: 'from-indigo-50 to-white border-indigo-200',badgeBg: 'bg-indigo-100', badgeText: 'text-indigo-700' },
};

const DEFAULT_CFG = {
    icon: Paperclip,
    badge: 'File',
    bg: 'from-orange-50 to-white border-orange-200',
    badgeBg: 'bg-orange-100',
    badgeText: 'text-orange-700',
};

function formatBytes(bytes: number): string {
    if (!bytes || bytes === 0) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getExt(data: any): string {
    // Try mimeType first (e.g. "application/pdf" → "pdf")
    if (data.mimeType) {
        const parts = data.mimeType.split('/');
        const sub = parts[parts.length - 1].toLowerCase();
        // Map MIME sub-types to extensions
        if (sub === 'pdf') return 'pdf';
        if (sub.includes('wordprocessingml')) return 'docx';
        if (sub.includes('spreadsheetml')) return 'xlsx';
        if (sub.includes('msword')) return 'doc';
        if (sub.includes('ms-excel')) return 'xls';
        if (['png', 'jpeg', 'gif', 'webp', 'tiff'].includes(sub)) return sub === 'jpeg' ? 'jpg' : sub;
    }
    // Fall back to file extension
    const name = data.fileName || data.label || '';
    return (name.split('.').pop() || '').toLowerCase();
}

const FileObjectNode = ({ data, selected }: NodeProps) => {
    const hasFile = Boolean(data.filePath);
    const fileName: string = data.label || data.fileName || 'File Object';
    const ext = getExt(data);
    const cfg = EXT_CONFIG[ext] ?? DEFAULT_CFG;
    const Icon = hasFile ? cfg.icon : UploadCloud;
    const sizeLabel = formatBytes(data.sizeBytes ?? 0);

    // When no file uploaded yet: show distinct "empty" state
    if (!hasFile && !data._uploading) {
        return (
            <BaseNodeUI
                title={data?.label || "File Object"}
                icon={UploadCloud}
                colorClass=""
                bgClass="from-gray-50 to-white border-gray-200"
                selected={selected}
                isSource={true}
                isTarget={false}
                executionStatus={data?.executionStatus}
            >
                <div className="flex flex-col items-center gap-0.5 py-0.5 text-center">
                    <span className="text-[9px] text-gray-400 leading-tight">
                        Drop PDF / DOCX / Image
                    </span>
                    <span className="text-[9px] text-orange-400 font-medium">
                        or use panel →
                    </span>
                </div>
            </BaseNodeUI>
        );
    }

    return (
        <BaseNodeUI
            title={data?.label || (hasFile ? fileName : 'File Object')}
            icon={Icon}
            colorClass=""
            bgClass={cfg.bg}
            selected={selected}
            isSource={true}
            isTarget={false}
            isRunning={data?.executionStatus === 'Running'}
            executionStatus={data?.executionStatus}
        >
            <div className="flex items-center gap-1.5 text-[10px] text-slate-500 font-medium tracking-tight">
                {data._uploading ? (
                    <span className="text-orange-500 animate-pulse">Uploading...</span>
                ) : (
                    <>
                        <span className={`px-1.5 py-0.5 rounded ${cfg.badgeBg} ${cfg.badgeText} font-semibold text-[9px]`}>
                            {cfg.badge}
                        </span>
                        {sizeLabel && <span className="text-gray-400">{sizeLabel}</span>}
                    </>
                )}
            </div>
        </BaseNodeUI>
    );
};

export default memo(FileObjectNode);
